import { getFormErrorMessage } from "@core/utility/get-error-message";
import { QuoteTransportFormRule, QuoteTransportFormSchema } from "@factory/form/form.rule";
import { useFormDefaultQuoteTransport } from "@factory/form/form-default-quote-transport.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseQuoteTransportModel } from "@factory/model/query-model-quote-transport";
import { 
  useQyDeleteQuoteTransport, 
  useQyGetQuoteTransportById, 
  useQyUpdateQuoteTransport} from "@factory/query/quote-transport.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditQuoteTransport() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultQuoteTransport();
  const navigate = useNavigate();
  const { mapUpdateQuoteTransport, mapDeleteQuoteTransport } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { quoteTransportId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID QuoteTransport
  const handleGetApiSuccess = (data: GetResponseQuoteTransportModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("quotation", responseData?.quotation || "");
      setValue("transportType", responseData?.transport_type || "");
      setValue("fromLocation", responseData?.from_location || "");
      setValue("toLocation", responseData?.to_location || "");
      setValue("pricePerPax", responseData?.price_per_pax);
      setValue("numPeople", responseData?.num_people);
      setValue("unit", responseData?.unit || "");
      setValue("amount", responseData?.amount);
  };
  const {
    data: quoteTransportResponse,
    isLoading,
    isError: quoteTransportError,
  } = useQyGetQuoteTransportById(quoteTransportId || "", handleGetApiSuccess);
  const quoteTransportData = quoteTransportResponse?.data?.[0];

  // API UPDATE QuoteTransport
  const handleUpdateApiSuccess = () => {
    showSuccess("QuoteTransport updated successfully");
  };
  const { mutate: updateQuoteTransport, isLoading: isEditLoading } = useQyUpdateQuoteTransport(
    handleUpdateApiSuccess
  );

  // API DELETE QuoteTransport
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("QuoteTransport deleted successfully");
  };
  const { mutate: deleteQuoteTransport } = useQyDeleteQuoteTransport(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<QuoteTransportFormSchema>({
    defaultValues: getDefault(quoteTransportData),
    resolver: zodResolver(QuoteTransportFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: QuoteTransportFormSchema) => {
    if (!quoteTransportData || !quoteTransportData.code) {
      throw new Error("No quoteTransport data");
    }

    const formData = mapUpdateQuoteTransport(form, quoteTransportData.code);
    updateQuoteTransport(formData);
  };
  const handleValidateError = (err: FieldErrors<QuoteTransportFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!quoteTransportData || !quoteTransportData.code) {
      throw new Error("No quoteTransport data found");
    }

    const form = mapDeleteQuoteTransport(quoteTransportData.code);
    deleteQuoteTransport(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    quoteTransportId,
    quoteTransportData,
    isLoading,
    quoteTransportError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}